'use client';
import type { ReactNode} from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { type OverridedMixpanel } from 'mixpanel-browser';

import type { DeserializedUser } from '@api';

import type { MixpanelEvent, MixpanelEventName } from '@shared/api/mixPanel';
import { useAuth } from '@shared/common/providers/AuthProvider';
import { getProcessEnvGlobalConfig } from '@shared/config/global';
import useMixPanelToggles from '@shared/hooks/useMixPanelToggles';

import { createSafeContext, useSafeContext } from '@utilities/context';

interface ContextValue {
  track: CustomTrack;
  user?: DeserializedUser | null;
}

const Context = createSafeContext<ContextValue>();

export const useMixPanel = () => useSafeContext(Context);

const isDev = getProcessEnvGlobalConfig('isDev');
const mixPanelProjectToken = process.env.NEXT_PUBLIC_MIXPANEL_PROJECT_TOKEN || '';

type CustomTrack = <TEventName extends MixpanelEventName>(
  eventName: TEventName,
  properties: MixpanelEvent[TEventName],
  optionsOrCallback?: Parameters<OverridedMixpanel['track']>[2],
  callback?: Parameters<OverridedMixpanel['track']>[3],
) => ReturnType<OverridedMixpanel['track']>;

interface MixPanelProviderProps {
  children?: ReactNode;
}

export const MixPanelProvider = ({ children }: MixPanelProviderProps) => {
  const { user } = useAuth();

  const { getIsMixPanelEventEnabled } = useMixPanelToggles();

  const [mixpanelApi, setMixpanelApi] = useState<OverridedMixpanel | null>(null);
  const [deferredEvents, setDeferredEvents] = useState<
    Array<Parameters<OverridedMixpanel['track']>>
  >([]);

  useEffect(() => {
    import('mixpanel-browser').then(({ default: mixpanel }) => setMixpanelApi(mixpanel));
  }, []);

  useEffect(() => {
    if (mixpanelApi) {
      mixpanelApi.init(mixPanelProjectToken, {
        debug: isDev,
        persistence: 'localStorage',
      });

      if (!user) {
        mixpanelApi.identify(mixpanelApi.get_distinct_id());
      } else {
        mixpanelApi.identify(String(user.id));
        mixpanelApi.people.set({
          $name: user.attributes.name,
          $email: user.attributes.email?.replace(/(.{2}).+(@.+)/, '$1***$2'),
          plan: user.membershipTier.attributes.key,
        });
      }
    }
  }, [user, mixpanelApi]);

  const sendEvent = useCallback(
    (api: OverridedMixpanel, ...args: Parameters<OverridedMixpanel['track']>) => {
      try {
        api.track(...args);
        console.debug(`Event tracked: Tracking '${args[0]}'.`, { args });
      } catch (error) {
        console.debug(`Error tracking event '${args[0]}':`, error);
      }
    },
    [],
  );

  useEffect(() => {
    if (mixpanelApi && deferredEvents.length > 0) {
      deferredEvents.forEach((event) => sendEvent(mixpanelApi, ...event));

      setDeferredEvents([]);
    }
  }, [deferredEvents, mixpanelApi, sendEvent]);

  const track = useCallback<CustomTrack>(
    (eventName, ...args) => {
      if (!getIsMixPanelEventEnabled(eventName)) {
        console.debug(`Event not tracked: Tracking for event '${eventName}' is disabled.`);
        return;
      }

      if (mixpanelApi) {
        sendEvent(mixpanelApi, eventName, ...args);
      } else {
        setDeferredEvents((value) => [...value, [eventName, ...args]]);
      }
    },
    [getIsMixPanelEventEnabled, mixpanelApi, sendEvent],
  );

  return <Context.Provider value={{ track, user }}>{children}</Context.Provider>;
};

export const useTrackMixPanel = (...args: Parameters<CustomTrack>) => {
  const { track } = useMixPanel();

  // Stringify the arguments to ensure stability
  const memoizedArgs = useMemo(() => JSON.stringify(args), [args]);

  useEffect(() => {
    if (args.length > 0) {
      // Deserialize the memoizedArgs back into their original form as a tuple
      const parsedArgs = JSON.parse(memoizedArgs) as Parameters<CustomTrack>;
      track(...parsedArgs);
    }
  }, [track, memoizedArgs, args.length]);
};
